import {
  InformationType,
  POPULATION_TYPE,
  SensitivityLevelHelper,
} from "@/common/models";
import { SharedState } from "@/common/state";
import { indexBy, uniqby } from "@/fp";
import { cord, formatInput, gql2 } from "@/gql/lib";
import {
  ActionEnum,
  AxisType,
  QueryTier1countArgs,
  TimeAxisRangeInput,
} from "@/gql/types";
import { BaseDashboardItem } from "../data/models";

const info_type_per_l1_query_input = (
  frame: TimeAxisRangeInput
): QueryTier1countArgs => {
  return {
    params: {
      coordinate: {
        x: cord(AxisType.None),
        y: cord(AxisType.ContentLabel),
        z: frame,
      },
      pageSize: 1000,
      cursor: "",
      action: ActionEnum.List,
    },
  };
};

type N_L_M_H = [number, number, number, number];

export const calc_ms_labels_per_info_type = gql2<
  void,
  { ms_labels: Record<string, N_L_M_H>; v_labels: Record<string, N_L_M_H> }
>("what.ms-labels-coverage", (_, { frame }) => {
  const indexed_ms_labels = indexBy<InformationType>("id")([
    ...SharedState.abstract_has_mip_labels.value,
  ]);
  const indexed_vlabels = indexBy<InformationType>("id")([
    ...SharedState.virtualLabels.value,
  ]);

  const ms_labels_ids = SharedState.abstract_has_mip_labels.value.map(
    (d) => d.id
  );

  const vlabels_ids = SharedState.virtualLabels.value.map((d) => d.id);

  return {
    query: `{
     vLabels: tier1count(params:${formatInput({
       coordinate: {
         x: cord(AxisType.None),
         y: {
           type: AxisType.ContentLabel,
           values: vlabels_ids,
         },
         z: frame,
       },
       cursor: "",
       action: ActionEnum.List,
     })}){
        y{value}
        count
      }

      msLabels: tier1count(params:${formatInput({
        coordinate: {
          x: cord(AxisType.None),
          y: {
            type: AxisType.ContentLabel,
            values: ms_labels_ids,
          },
          z: frame,
        },
        cursor: "",
        action: ActionEnum.List,
      })}){
        y{value}
        count
      }
  }`,
    mapper: (res) => {
      const { msLabels, vLabels } = res;
      const l0_count_for_ms_labels = msLabels
        .filter((item: any) => indexed_ms_labels[item.y.value])
        .reduce(
          (
            acc: Record<string, N_L_M_H>,
            item: { count: number; y: { value: string } }
          ) => {
            const label = indexed_ms_labels[item.y.value];
            const l0 = label.parentId as string;
            const sensitivity = SensitivityLevelHelper.floatToInt(
              label.sensitivity
            );
            if (acc[l0]) {
              acc[l0][sensitivity] = item.count;
            } else {
              const arr = Array(4).fill(0) as N_L_M_H;
              acc[l0] = arr;
              acc[l0][sensitivity] = item.count;
            }
            return acc;
          },
          {} as Record<string, N_L_M_H>
        );

      const l0_count_for_v_labels = vLabels
        .filter((item: any) => indexed_vlabels[item.y.value])
        .reduce(
          (
            acc: Record<string, N_L_M_H>,
            item: { count: number; y: { value: string } }
          ) => {
            const label = indexed_vlabels[item.y.value];
            const l0 = label.infoTypeCategoryId as string;
            const sensitivity = SensitivityLevelHelper.floatToInt(
              label.sensitivity
            );
            if (acc[l0]) {
              acc[l0][sensitivity] = item.count;
            } else {
              const arr = Array(4).fill(0) as N_L_M_H;
              acc[l0] = arr;
              acc[l0][sensitivity] = item.count;
            }
            return acc;
          },
          {} as Record<string, N_L_M_H>
        );

      return {
        ms_labels: l0_count_for_ms_labels,
        v_labels: l0_count_for_v_labels,
      };
    },
  };
});

export const info_type_per_l1 = gql2<
  void,
  { other: number; l1s: BaseDashboardItem[] }
>("mapping-tier1.files", (_, { frame, allInfoTypes }) => {
  return {
    query: `
        {
            total: tier1count(params:${formatInput(
              info_type_per_l1_query_input(frame).params
            )}){
              y{value}
              count
            }
        }
        `,
    mapper: (res) => {
      const { total } = res;
      const other_id = SharedState.default_label.value?.id as string;
      let other_count = 0;
      const other = total.find((d: any) => d.y.value === other_id);
      if (other) {
        other_count = other.count;
      }

      const result = uniqby("id")(
        total
          .filter(
            (d: any) =>
              allInfoTypes[d.y.value] &&
              allInfoTypes[d.y.value].generation === 1
          )
          .map((item: { y: { value: string }; count: number }) => {
            const { y, count } = item;
            const label = allInfoTypes[y.value];
            return {
              id: label.id,
              name: label.labelName,
              count,
              parent_id: label.parentId,
              type: POPULATION_TYPE.LABEL,
            };
          })
      );
      return {
        l1s: result,
        other: other_count,
      };
    },
  };
});

const get_personal_info_counts_query_input = (
  frame: TimeAxisRangeInput,
  ids: string[]
): QueryTier1countArgs => {
  return {
    params: {
      coordinate: {
        x: cord(AxisType.None),
        y: {
          type: AxisType.ContentLabel,
          values: ids,
        },
        z: frame,
      },
      pageSize: 1000,
      cursor: "",
      action: ActionEnum.List,
    },
  };
};

export const get_personal_info_counts = gql2<string[], any[]>(
  "mapping-tier1.personal",
  (personal_info_ids, { frame, allInfoTypes }) => {
    return {
      query: `
        {
            total: tier1count(params:${formatInput(
              get_personal_info_counts_query_input(frame, personal_info_ids)
                .params
            )}){
              y{value}
              count
            }
        }
        `,
      mapper: (res) => {
        const { total } = res;
        const result = uniqby("id")(
          total
            .filter((item: any) => allInfoTypes[item.y.value])
            .map((item: { y: { value: string }; count: number }) => {
              const { y, count } = item;
              const label = allInfoTypes[y.value];
              return {
                id: label.id,
                name: label.labelName,
                parent_code: label.content_code,
                count,
              };
            })
        );
        return result;
      },
    };
  }
);
